import { useEffect, useMemo, useState } from 'react'
import {
  fetchBusinessEntities,
  fetchDivisions,
  fetchSubdivisions,
  updateMyProfile,
  type BusinessEntityDto,
  type DivisionDto,
  type SubdivisionDto,
} from '../services/api'
import { BUSINESS_LEVEL_LABELS, BUSINESS_LEVEL_VALUES, type BusinessLevelValue } from '../utils/businessLevelLabels'
import { useAuth, type AuthUser } from '../context/AuthContext'
import './CompleteProfileModal.css'

function mapProfileToUser(profile: Awaited<ReturnType<typeof updateMyProfile>>, prev: AuthUser | null): AuthUser {
  return {
    ...(prev ?? { userId: profile.userId, displayName: profile.displayName }),
    userId: profile.userId,
    displayName: profile.displayName,
    email: profile.email,
    role: profile.role,
    businessLevel: profile.businessLevel,
    functionalRoles: profile.functionalRoles,
    divisionId: profile.divisionId,
    subdivisionId: profile.subdivisionId,
    businessEntityIds: profile.businessEntityIds,
    divisionName: profile.divisionName,
    subdivisionName: profile.subdivisionName,
    businessEntityNames: profile.businessEntityNames,
    isAutoProvisioned: profile.isAutoProvisioned,
    isProfileComplete: profile.isProfileComplete,
    entraObjectId: profile.entraObjectId,
    entraTenantId: profile.entraTenantId,
    entraPreferredUsername: profile.entraPreferredUsername,
    entraName: profile.entraName,
    entraIdentityProvider: profile.entraIdentityProvider,
    isImpersonating: profile.isImpersonating,
    impersonatorUserId: profile.impersonatorUserId,
    impersonatorDisplayName: profile.impersonatorDisplayName,
    authMethod: prev?.authMethod,
  }
}

export default function CompleteProfileModal({ open }: { open: boolean }) {
  const { user, login, token } = useAuth()
  const [businessLevel, setBusinessLevel] = useState<BusinessLevelValue>('Business')
  const [divisionId, setDivisionId] = useState('')
  const [subdivisionId, setSubdivisionId] = useState('')
  const [businessEntityIds, setBusinessEntityIds] = useState<string[]>([])
  const [divisions, setDivisions] = useState<DivisionDto[]>([])
  const [subdivisions, setSubdivisions] = useState<SubdivisionDto[]>([])
  const [businessEntities, setBusinessEntities] = useState<BusinessEntityDto[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const selectableLevels = useMemo(
    () => BUSINESS_LEVEL_VALUES.filter((l) => l !== 'MasterAdmin' || user?.businessLevel === 'MasterAdmin'),
    [user?.businessLevel],
  )

  useEffect(() => {
    if (!open) return
    setBusinessLevel((user?.businessLevel as BusinessLevelValue) || 'Business')
    setDivisionId(user?.divisionId || '')
    setSubdivisionId(user?.subdivisionId || '')
    setBusinessEntityIds(user?.businessEntityIds || [])
    setError(null)
    void fetchDivisions().then(setDivisions).catch(() => setDivisions([]))
  }, [open, user])

  useEffect(() => {
    if (!open) return
    void fetchSubdivisions(divisionId ? { divisionId } : undefined)
      .then(setSubdivisions)
      .catch(() => setSubdivisions([]))
  }, [open, divisionId])

  useEffect(() => {
    if (!open) return
    void fetchBusinessEntities(subdivisionId ? { subdivisionId } : undefined)
      .then(setBusinessEntities)
      .catch(() => setBusinessEntities([]))
  }, [open, subdivisionId])

  if (!open) return null

  const onSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const profile = await updateMyProfile({
        businessLevel,
        divisionId: businessLevel === 'Division' || businessLevel === 'MasterAdmin' ? divisionId || undefined : undefined,
        subdivisionId: businessLevel === 'Subdivision' ? subdivisionId || undefined : undefined,
        businessEntityIds: businessLevel === 'Business' ? businessEntityIds : undefined,
      })
      if (token) {
        login(token, mapProfileToUser(profile, user))
      }
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (e instanceof Error ? e.message : 'Failed to save profile')
      setError(msg)
    } finally {
      setSaving(false)
    }
  }

  const toggleBe = (id: string) => {
    setBusinessEntityIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  return (
    <div className="complete-profile-overlay" role="dialog" aria-modal="true" aria-labelledby="complete-profile-title">
      <div className="complete-profile-modal">
        <h2 id="complete-profile-title">Complete your profile</h2>
        <p className="complete-profile-lead">
          Select your business level and division scope before using PCR upload, website checks, and other compliance tools.
        </p>

        <label className="complete-profile-field">
          <span>Business level</span>
          <select
            value={businessLevel}
            onChange={(e) => {
              setBusinessLevel(e.target.value as BusinessLevelValue)
              setDivisionId('')
              setSubdivisionId('')
              setBusinessEntityIds([])
            }}
          >
            {selectableLevels.map((level) => (
              <option key={level} value={level}>
                {BUSINESS_LEVEL_LABELS[level]}
              </option>
            ))}
          </select>
        </label>

        {businessLevel === 'Division' && (
          <label className="complete-profile-field">
            <span>Division</span>
            <select value={divisionId} onChange={(e) => setDivisionId(e.target.value)}>
              <option value="">Select division…</option>
              {divisions.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
        )}

        {businessLevel === 'Subdivision' && (
          <>
            <label className="complete-profile-field">
              <span>Division (filter)</span>
              <select value={divisionId} onChange={(e) => { setDivisionId(e.target.value); setSubdivisionId('') }}>
                <option value="">All divisions</option>
                {divisions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="complete-profile-field">
              <span>Regional (subdivision)</span>
              <select value={subdivisionId} onChange={(e) => setSubdivisionId(e.target.value)}>
                <option value="">Select subdivision…</option>
                {subdivisions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}

        {businessLevel === 'Business' && (
          <>
            <label className="complete-profile-field">
              <span>Division (filter)</span>
              <select value={divisionId} onChange={(e) => { setDivisionId(e.target.value); setSubdivisionId(''); setBusinessEntityIds([]) }}>
                <option value="">All divisions</option>
                {divisions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="complete-profile-field">
              <span>Regional (filter)</span>
              <select value={subdivisionId} onChange={(e) => { setSubdivisionId(e.target.value); setBusinessEntityIds([]) }}>
                <option value="">All subdivisions</option>
                {subdivisions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
            <fieldset className="complete-profile-field">
              <legend>Business entities (select at least one)</legend>
              <div className="complete-profile-checklist">
                {businessEntities.length === 0 && <p className="complete-profile-muted">No business entities found for this filter.</p>}
                {businessEntities.map((be) => (
                  <label key={be.id} className="complete-profile-check">
                    <input
                      type="checkbox"
                      checked={businessEntityIds.includes(be.id)}
                      onChange={() => toggleBe(be.id)}
                    />
                    {be.name}
                  </label>
                ))}
              </div>
            </fieldset>
          </>
        )}

        {businessLevel === 'MasterAdmin' && (
          <>
            <p className="complete-profile-muted">Group level can see every division.</p>
            <label className="complete-profile-field">
              <span>Division for PCR import</span>
              <select value={divisionId} onChange={(e) => setDivisionId(e.target.value)}>
                <option value="">No division</option>
                {divisions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>
            <p className="complete-profile-muted">PCR imports are stored in this division. It does not limit what you can see.</p>
          </>
        )}

        {error && <p className="complete-profile-error">{error}</p>}

        <div className="complete-profile-actions">
          <button type="button" className="mim-btn mim-btn-primary" onClick={() => void onSave()} disabled={saving}>
            {saving ? 'Saving…' : 'Save and continue'}
          </button>
        </div>
      </div>
    </div>
  )
}
